import range from "lodash/range";
import { jsx } from "panel";
import { SvgIconEl } from "common/components/svg-icon";
import { TooltipEl } from "common/components/tooltip";
import { Placement } from "common/components/tooltip/types";
import { PanelEl } from "common/components/panel";
import { Direction } from "common/components/panel/types";
import { handleKeyPress } from "common/util/focusable";
import { SidenavItemEl } from "./sidenav-item";
import { CtaIconTheme } from "./types";
import { ScrollPosition, SIDENAV_DEFAULT_WIDTH, SIDENAV_MAX_WIDTH, SIDENAV_MIN_WIDTH } from "./constants";
function Loader() {
  return /* @__PURE__ */ jsx("div", {
    sel: ".loader"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".mp-skeleton-header"
  }), range(9).map((_) => /* @__PURE__ */ jsx("div", {
    sel: ".mp-skeleton-large"
  })));
}
function SidenavItemWithNestedItems({
  section,
  item,
  idx,
  sectionIdx,
  selectedItemInSection,
  hoveredItemValues,
  sectionDragDisabled,
  activeDragSectionIdx,
  onItemDrop,
  onItemDragStart,
  onItemDragEnd,
  onSelectItem,
  toggleItemChildren,
  onItemOverflowMenuChange,
  onItemOverflowMenuSelect,
  onItemMouseEntered,
  confirmNavigateFunc,
  isNestedDashboardsEnabled,
  expandedItemSet,
  deepLevel = 0,
  parentItemId = ``
}) {
  var _a, _b;
  const itemId = parentItemId ? `${parentItemId}-${item.value}` : `${sectionIdx}-${item.value}`;
  return /* @__PURE__ */ jsx("div", {
    sel: ".sidenav-item"
  }, /* @__PURE__ */ jsx(SidenavItemEl, {
    attrs: {
      "is-selected": selectedItemInSection && item.value === selectedItemInSection.value,
      "is-new": deepLevel === 0 && item.isNew && !(hoveredItemValues == null ? void 0 : hoveredItemValues.has(item.value)),
      "is-draggable": deepLevel === 0 && section.dragDropEnabled,
      "is-disabled": deepLevel === 0 && sectionDragDisabled,
      "can-drop-item": deepLevel === 0 && Number.isInteger(activeDragSectionIdx),
      "label-tab-size": deepLevel,
      "is-nested-dashboards-enabled": isNestedDashboardsEnabled,
      "has-expand-caret": ((_a = item.nestedItems) == null ? void 0 : _a.length) > 0,
      "is-expanded": expandedItemSet.has(itemId)
    },
    on: {
      itemDrop: onItemDrop,
      itemDragStart: onItemDragStart,
      itemDragEnd: onItemDragEnd,
      itemClick: onSelectItem,
      itemExpand: toggleItemChildren,
      overflowMenuChange: onItemOverflowMenuChange,
      overflowMenuSelect: onItemOverflowMenuSelect,
      mouseenter: () => onItemMouseEntered(item)
    },
    props: { confirmNavigateFunc, item: { ...item, itemIdx: idx, sectionIdx, itemId } }
  }), /* @__PURE__ */ jsx("div", {
    sel: ".sidenav-nested-items",
    class: { "is-selected": (selectedItemInSection == null ? void 0 : selectedItemInSection.value) === item.value }
  }, expandedItemSet.has(itemId) && ((_b = item.nestedItems) == null ? void 0 : _b.length) ? item.nestedItems.map((subItem) => /* @__PURE__ */ jsx(SidenavItemWithNestedItems, {
    section,
    item: subItem,
    sectionIdx,
    idx,
    selectedItemInSection,
    hoveredItemValues,
    sectionDragDisabled,
    activeDragSectionIdx,
    onItemDrop,
    onItemDragStart,
    onItemDragEnd,
    onSelectItem,
    toggleItemChildren,
    onItemOverflowMenuChange,
    onItemOverflowMenuSelect,
    onItemMouseEntered,
    confirmNavigateFunc,
    deepLevel: deepLevel + 1,
    isNestedDashboardsEnabled,
    expandedItemSet,
    parentItemId: itemId
  })) : null));
}
function SidenavSectionItemsWrapper({ isExpanded }, sectionItemsChildren) {
  if (isExpanded) {
    return /* @__PURE__ */ jsx("div", {
      sel: ".section-items-list"
    }, sectionItemsChildren);
  }
  return null;
}
function SidenavCtaEl({
  cta,
  isExpanded,
  isSelected,
  onClick
}) {
  const iconRenderer = () => {
    var _a;
    return /* @__PURE__ */ jsx("div", {
      sel: ".icon-wrapper",
      class: { [`${(_a = cta.iconTheme) != null ? _a : CtaIconTheme.Light}-theme`]: true, "is-selected": isSelected }
    }, /* @__PURE__ */ jsx(SvgIconEl, {
      attrs: { icon: cta.icon }
    }));
  };
  if (isExpanded) {
    return /* @__PURE__ */ jsx(SidenavItemEl, {
      sel: ".cta-item",
      props: { iconRenderer, item: cta },
      attrs: { "is-selected": isSelected },
      on: { itemClick: (ev) => onClick(ev.detail.value) }
    });
  } else {
    return /* @__PURE__ */ jsx("a", {
      sel: ".cta-item.collapsed",
      on: { click: () => onClick(cta.value), keypress: handleKeyPress },
      attrs: { value: cta.value, tabindex: 0, ...cta.href ? { href: cta.href } : {} }
    }, iconRenderer(), /* @__PURE__ */ jsx(TooltipEl, {
      attrs: { placement: Placement.Right }
    }, cta.label));
  }
}
function SidenavSection({
  section,
  isExpanded,
  sectionDragDisabled,
  onToggleSection
}, sectionItemsChildren) {
  const isSectionCollapsible = section.label && section.isCollapsible;
  return /* @__PURE__ */ jsx("div", {
    sel: ".section",
    class: { "active-dragging-disabled": sectionDragDisabled }
  }, isSectionCollapsible && /* @__PURE__ */ jsx("div", {
    sel: ".section-header",
    attrs: { tabindex: 0 },
    on: { click: () => onToggleSection(section.label), keypress: handleKeyPress }
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    sel: ".caret",
    attrs: {
      icon: isExpanded ? `caret-down` : `caret-right`
    }
  }), /* @__PURE__ */ jsx("div", {
    sel: ".section-header-label"
  }, section.label)), /* @__PURE__ */ jsx(SidenavSectionItemsWrapper, {
    isExpanded: isSectionCollapsible ? isExpanded : true
  }, sectionItemsChildren));
}
export function template() {
  var _a;
  const allSelectedIndices = this.helpers.getAllSelectedIndices();
  const sections = this.state.sections;
  const sidenavContent = this.attr(`loading`) ? /* @__PURE__ */ jsx(Loader, null) : sections.map((section, sectionIdx) => {
    const sectionDragDisabled = Number.isInteger(this.state.activeDragSectionIdx) && this.state.activeDragSectionIdx !== sectionIdx;
    const isExpanded = !this.state.collapsedSectionSet.has(section.label);
    const { itemIndex: selectedSidenavIndex } = allSelectedIndices.find((selectedIndices) => selectedIndices.sectionIndex === sectionIdx) || {};
    const selectedItemInSection = selectedSidenavIndex > -1 ? sections[sectionIdx].items[selectedSidenavIndex] : null;
    if (section.items.length === 0) {
      return null;
    }
    return /* @__PURE__ */ jsx(SidenavSection, {
      section,
      isExpanded,
      sectionDragDisabled,
      onToggleSection: this.helpers.toggleSection
    }, isExpanded && section.items.map((item, idx) => /* @__PURE__ */ jsx("div", {
      sel: ".sidenav-item",
      attrs: { value: item.value },
      hook: {
        insert: this.helpers.handleSidenavItemInserted
      }
    }, /* @__PURE__ */ jsx(SidenavItemWithNestedItems, {
      section,
      item,
      idx,
      sectionIdx,
      selectedItemInSection,
      hoveredItemValues: this.state.hoveredItemValues,
      sectionDragDisabled,
      activeDragSectionIdx: this.state.activeDragSectionIdx,
      onItemDrop: this.helpers.onItemDrop,
      onItemDragStart: this.helpers.onItemDragStart,
      onItemDragEnd: this.helpers.onItemDragEnd,
      onSelectItem: this.helpers.onSelectItem,
      toggleItemChildren: this.helpers.toggleItemChildren,
      onItemOverflowMenuChange: this.helpers.onItemOverflowMenuChange,
      onItemOverflowMenuSelect: this.helpers.onItemOverflowMenuSelect,
      onItemMouseEntered: this.helpers.onItemMouseEntered,
      confirmNavigateFunc: this.confirmNavigateFunc,
      isNestedDashboardsEnabled: this.attr(`is-nested-dashboards-enabled`),
      expandedItemSet: this.state.expandedItemSet
    }))));
  });
  const header = this.attr(`header`);
  const footer = this.attr(`footer`);
  return /* @__PURE__ */ jsx(PanelEl, {
    sel: ".sidebar",
    attrs: {
      "storage-key": this.attr(`storage-key`),
      "min-adjacent-size": this.attr(`min-adjacent-width`),
      "max-size": SIDENAV_MAX_WIDTH,
      "min-size": SIDENAV_MIN_WIDTH,
      "panel-name": `Side Nav`,
      "resize-direction": Direction.Right,
      "default-ratio": 0.15,
      "default-size": SIDENAV_DEFAULT_WIDTH,
      "fixed-size": this.attr(`fixed-size`),
      "show-collapse-button": !this.attr(`fixed-size`),
      ...(footer == null ? void 0 : footer.actions) ? { "extra-actions": footer == null ? void 0 : footer.actions } : {},
      "style-override": `.wrapper .content-footer { border-color: var(--gray2); }`
    },
    on: {
      panelResize: (ev) => {
        const width = ev.detail.size;
        const isCollapsed = width < SIDENAV_MIN_WIDTH;
        this.update({ isCollapsed });
        this.dispatchEvent(new CustomEvent(`sidenavResize`, { detail: { width } }));
      },
      actionclick: (ev) => this.helpers.onFooterActionClick(ev.detail.value)
    },
    props: { containerEl: this.parentElement }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".wrapper",
    class: {
      collapsed: this.state.isCollapsed
    },
    attrs: { elref: `wrapper` }
  }, header ? /* @__PURE__ */ jsx("div", {
    sel: ".header",
    attrs: { elref: `sidenavHeader` },
    class: {
      "is-expanded-style": this.helpers.isExpandedStyle(),
      "bottom-border": ![ScrollPosition.Top, ScrollPosition.None].includes(this.state.scrollPosition) && this.helpers.isExpandedStyle(),
      "slot-header": `slot` in header,
      "actions-container": `actions` in header
    }
  }, !this.state.isCollapsed ? /* @__PURE__ */ jsx("div", {
    sel: ".title"
  }, header.title) : null, `slot` in header ? /* @__PURE__ */ jsx("slot", {
    attrs: {
      name: this.state.isCollapsed && header.collapsedSlot ? header.collapsedSlot : header.slot
    }
  }) : (_a = header.actions) == null ? void 0 : _a.map((cta) => /* @__PURE__ */ jsx(SidenavCtaEl, {
    cta,
    isExpanded: this.helpers.isExpandedStyle(),
    isSelected: this.attr(`selected`) === cta.value,
    onClick: this.helpers.onHeaderActionClick
  }))) : null, !this.state.isCollapsed ? /* @__PURE__ */ jsx("div", {
    sel: ".sections-container",
    hook: { insert: this.helpers.onSectionsInserted },
    attrs: { elref: `sectionsContainer` }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".sections"
  }, sidenavContent)) : null));
}
